import React, { Fragment, useCallback, useEffect, useState } from "react"

import ReactGA from "react-ga4"
import { useTranslation } from "react-i18next"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { useNavigate } from "react-router-dom"

import * as Yup from "yup"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import CloseIcon from "@mui/icons-material/Close"
import { Card, CardHeader, Grid, IconButton, Link, Step, StepContent, StepLabel, Stepper } from "@mui/material"
import { Button, NotificationUtils, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { FormikProvider, useFormik } from "formik"
import type { OpenAPIV3 } from "openapi-types"

import { getTheme } from "../../hooks/UseTheme"
import { KonanAPI } from "../../services/KonanAPI"
import type {
  CreateAutoMlRequest,
  CreateModelRequest,
  CreateModelResponse,
  CreateProjectRequest,
  CustomFeatureRequest,
  TestPrediction,
  TrainModelUsingPrebuiltModelRequest,
  baseErrorType,
} from "../../types/custom/projects"
import type { Deployment } from "../../types/generated/api/Deployment"
import type { ExcludedFeatureRequest } from "../../types/generated/api/ExcludedFeatureRequest"
import type { ModelSettingsSetRequest } from "../../types/generated/api/ModelSettingsSetRequest"
import type { TestPredictionResponse } from "../../types/generated/api/TestPredictionResponse"
import type { TrainingDataSchemaResponse } from "../../types/generated/api/TrainingDataSchemaResponse"
import { mapResponseTypesToTabValues } from "../../utils/deploymentDetailsHelpers"
import { getHostnames } from "../../utils/genericHelpers"
import { extractPredictionKeysFromOpenapi } from "../../utils/modelDetailsHelpers"
import { ConfigurationForm, DryRunForm, handleSubmit as getConfiguration } from "../ModelConfigurations"
import { DataMappingFlow } from "./components/DataMappingFlow"
import { ModelCreationForm } from "./components/ModelCreationForm"
import { PostCreationDialog } from "./components/PostCreationDialog"
import { ProjectCreationOverview } from "./components/ProjectCreationOverview"
import { UploadTrainingDataFlow } from "./components/UploadTrainingDataFlow"

import Styles from "./Projects.module.scss"

/**
 * Project creation flow dialog container
 * @return  {<React.ReactElement />}
 */
export function ProjectCreationFlow(): React.ReactElement {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const theme = getTheme()

  const { t } = useTranslation("projects")

  const [createdProjectId, setCreatedProjectId] = useState<string>("")
  const [activeStep, setActiveStep] = useState<number>(0)
  const [isBtnLoading, setIsBtnLoading] = useState<boolean>(false)
  const [requestJsonError, setRequestJsonError] = useState<boolean>(false)
  const [trainingDataId, setTrainingDataId] = useState<string>("")

  const [autoMlData, setAutoMlData] = useState<{
    mapping: Array<CustomFeatureRequest>
    excludedColumns: Array<ExcludedFeatureRequest>
  }>({
    mapping: [],
    excludedColumns: [],
  })

  // Project form validation schema object
  const projectValidationSchema = Yup.object({
    name: Yup.string()
      .max(30, t("creationFlow.validation.nameMaxLength"))
      .required(t("creationFlow.validation.required")),
    description: Yup.string().max(200, t("creationFlow.validation.descriptionMaxLength")),
    type: Yup.string().required(t("creationFlow.validation.required")),
  })

  // Model form validation schema object
  const modelValidationSchema = Yup.object({
    name: Yup.string().required(t("creationFlow.validation.required")),
    registry: Yup.string().required(t("creationFlow.validation.required")),
    image_url: Yup.string().required(t("creationFlow.validation.required")),
    exposed_port: Yup.number()
      .notOneOf([8008, 9090], t("creationFlow.validation.restrictedPort"))
      .required(t("creationFlow.validation.required")),
    docker_username: Yup.string().max(50, t("creationFlow.validation.dockerUsernameMaxLength")),
    docker_password: Yup.string().max(50, t("creationFlow.validation.dockerPasswordMaxLength")),
  })

  // Configuration form validation schema object
  const configurationValidationSchema = Yup.object({
    target: Yup.string().required(t("creationFlow.validation.required")),
    lowerLimit: Yup.number().required(t("creationFlow.validation.required")),
    upperLimit: Yup.number().required(t("creationFlow.validation.required")),
    step: Yup.number(),
  })

  //AutoML formik validation
  const autoMlValidationSchema = Yup.object({
    name: Yup.string().required(t("creationFlow.validation.required")),
    target_column: Yup.string().required(t("creationFlow.validation.required")),
    targetColumnType: Yup.string(),
  })

  // Request mutations
  const createProjectMutation = useMutation<AxiosResponse<Deployment>, AxiosError<baseErrorType>, CreateProjectRequest>(
    KonanAPI.createProject,
  )

  const createModelMutation = useMutation<
    AxiosResponse<CreateModelResponse>,
    AxiosError<baseErrorType>,
    CreateModelRequest
  >(KonanAPI.createModel)

  const predictionsTest = useMutation<AxiosResponse<TestPredictionResponse>, AxiosError<baseErrorType>, TestPrediction>(
    ({ model_uuid, requestJson, skip_config, abortSignal }) =>
      KonanAPI.sendModelPredictionTest(model_uuid, requestJson, skip_config, abortSignal),
  )

  const classificationConfigurationMutation = useMutation<
    AxiosResponse,
    AxiosError<baseErrorType>,
    ModelSettingsSetRequest
  >(KonanAPI.createModelConfiguration)

  const trainModelUsingPrebuiltModel = useMutation<
    AxiosResponse,
    AxiosError<baseErrorType>,
    TrainModelUsingPrebuiltModelRequest
  >(KonanAPI.trainModelUsingPrebuiltModel)

  // Fetch project docs using project ID in route
  const { isLoading: isLoadingOpenApiData, data: dryRunRequest } = useQuery<
    AxiosResponse<OpenAPIV3.Document>,
    AxiosError<baseErrorType>
  >(
    ["project-docs", createProjectMutation.data?.data.uuid],
    () => KonanAPI.fetchProjectDocs(createProjectMutation.data?.data.uuid as string),
    {
      retry: true,

      enabled: createModelMutation.isSuccess,
    },
  )

  const {
    mutateAsync: trainModelUsingAutoMlModel,
    isError: isAutoMlError,
    error,
    data: trainingModelAutomlData,
    isLoading,
    isSuccess: isAutomlSuccess,
  } = useMutation<AxiosResponse, AxiosError<baseErrorType>, CreateAutoMlRequest>(KonanAPI.trainModelUsingAutoMlModel)

  const { data: trainingSchema, isLoading: isTrainingSchemaLoading } = useQuery<TrainingDataSchemaResponse, AxiosError>(
    ["training-data-schema", trainingDataId],
    () => KonanAPI.fetchTrainingDataSchema(createProjectMutation.data?.data.uuid as string, trainingDataId),
    {
      enabled: !!trainingDataId,
    },
  )

  // Project creation form control
  const projectFormikValues = useFormik({
    initialValues: {
      name: "",
      description: "",
      type: "",
      category: "model",
    },
    validationSchema: projectValidationSchema,
    onSubmit: async (values) => {
      try {
        setIsBtnLoading(true)

        ReactGA.event({
          category: "Deployments",
          action: t("analytics.creatingDeployment"),
        })

        const response = await createProjectMutation.mutateAsync({
          name: values.name,
          type:
            values.type === "credit_scoring_auto-ml" || values.type === "credit-scoring_pre-built"
              ? "credit_scoring"
              : values.type,
          category: values.category,
          description: values.description || undefined,
        })
        setCreatedProjectId(response.data.uuid)
        setActiveStep(activeStep + 1)
        setIsBtnLoading(false)

        queryClient.invalidateQueries("projects")
      } catch {
        setIsBtnLoading(false)

        ReactGA.event({
          category: "Deployments",
          action: t("analytics.deploymentErrors"),
        })
      }
    },
  })

  // Model creation form control
  const modelFormikValues = useFormik({
    initialValues: {
      name: "",
      image_url: "",
      registry: "KCR",
      exposed_port: 8000,
      docker_username: "",
      docker_password: "",
    },
    validationSchema: modelValidationSchema,
    onSubmit: async (values) => {
      try {
        setIsBtnLoading(true)
        ReactGA.event({
          category: "Deployments",
          action: t("analytics.creatingModel"),
        })
        await createModelMutation.mutateAsync({
          project_uuid: createdProjectId,
          name: values.name,
          image_url: values.image_url,
          exposed_port: values.exposed_port,
          docker_password: values.docker_password || undefined,
          docker_username: values.docker_username || undefined,
          state: "live",
        })
        setIsBtnLoading(false)

        queryClient.invalidateQueries(["overviewModels", createdProjectId])
        queryClient.invalidateQueries("models")
      } catch {
        setIsBtnLoading(false)
        ReactGA.event({
          category: "Models",
          action: t("analytics.modelErrors"),
        })
      }
    },
  })

  // initial AutoML form control
  const AutoMlFormikValues = useFormik({
    initialValues: {
      name: "",
      target_column: "",
      targetColumnType: "default",
    },
    validationSchema: autoMlValidationSchema,
    onSubmit: async ({ name, target_column }) => {
      setIsBtnLoading(true)
      try {
        await trainModelUsingAutoMlModel({
          projectId: createdProjectId ?? "",
          name: name !== "" ? name : "",
          mapping: autoMlData?.mapping,
          excluded_columns: autoMlData?.excludedColumns,
          target_column: target_column,

          training_data: trainingDataId !== "" && trainingDataId !== "sample" ? trainingDataId : "",
        })
      } catch (error) {
        console.error(error)
      }
      setIsBtnLoading(false)
    },
  })

  const modelCreationError =
    (createModelMutation.error?.response?.status === 500 && t("creationFlow.errors.serverError")) ||
    createModelMutation.error?.response?.data.name ||
    createModelMutation.error?.response?.data.details

  const isFailedLogs = !!(createModelMutation.error?.response?.data as { container_logs: string } | undefined)
    ?.container_logs

  // Dry run form control
  const DryRunFormikValues = useFormik({
    initialValues: {
      request: "{}",
      // adding as a place holder
      model: "default",
    },
    validationSchema: Yup.object({
      request: Yup.string().required(t("creationFlow.validation.requestBodyRequired")),
    }),
    onSubmit: async (values) => {
      try {
        const requestJson = JSON.parse(values.request)
        setRequestJsonError(false)

        try {
          await predictionsTest.mutateAsync({
            model_uuid: createModelMutation.data?.data.model.uuid || "",
            requestJson: {
              ...requestJson,
            },
          })
        } catch (e) {
          console.warn(e)
        }
      } catch {
        setRequestJsonError(true)
      }
    },
  })

  // Model configuration form control
  const configurationFormikValues = useFormik({
    initialValues: {
      dataType: "",
      reDataType: "",
      target: "",
      lowerLimit: 0,
      upperLimit: 100,
      step: 1,
    },
    validationSchema: configurationValidationSchema,
    onSubmit: async () => {
      const requestObject = getConfiguration()

      // TODO:: Should be refactored to make the dialog use formik and validate using yup
      // cant do that now since i will have to refactor the whole form to make it work
      requestObject?.settings?.forEach((setting) => {
        if (setting.label === "") {
          NotificationUtils.toast(t("notifications.addLabelsError"), { severity: "error" })
          throw new Error(t("notifications.addLabelsError"))
        }
      })

      setIsBtnLoading(true)

      await classificationConfigurationMutation.mutateAsync({ ...requestObject }).then(() => {
        queryClient.invalidateQueries("activeConfig")
      })

      NotificationUtils.toast(t("notifications.modelConfigured"), { severity: "success" })

      setIsBtnLoading(false)
    },
  })

  // Dry run response
  const TestResponse = predictionsTest.isError
    ? predictionsTest.error?.response?.data.ml_service_output
    : predictionsTest.data?.data.output

  const responseData = !TestResponse
    ? t("creationFlow.responses.executeToSeeResponse")
    : JSON.stringify(TestResponse, null, "  ")

  /**
   * stepper helper function
   * to decide which disable helper function to use based on the project type
   * @return  {boolean}
   */
  const shouldDisableNext = (): boolean => {
    switch (projectFormikValues.values.type) {
      case "generic":
        return shouldDisableGeneric()
      case "classification":
        return shouldDisableClassification()
      case "credit_scoring_pre-built":
        return shouldDisableCreditScoring()
      case "credit_scoring_auto-ml":
        return shouldDisableCreditScoringAutoml()

      default:
        return shouldDisableGeneric()
    }
  }

  /**
   * credit scoring helper function
   * trains prebuilt model for credit scoring project
   * @return  {Promise<void>}
   */
  const handleTrainClick = async (type: "auto-ml" | "pre-built"): Promise<void> => {
    setIsBtnLoading(true)
    if (type === "pre-built") {
      await trainModelUsingPrebuiltModel.mutateAsync({
        projectId: createdProjectId,
        modelName: AutoMlFormikValues.values.name !== "" ? AutoMlFormikValues.values.name : undefined,
        trainingDataId: trainingDataId !== "" && trainingDataId !== "sample" ? trainingDataId : undefined,
      })
    }
    setIsBtnLoading(false)
  }

  const onAutoMlDataChange = useCallback(
    (mapping: Array<CustomFeatureRequest>, excludedColumns: Array<ExcludedFeatureRequest>) => {
      setAutoMlData({ mapping, excludedColumns })
    },
    [],
  )

  // Credit scoring steps with action buttons, description, etc...
  const creditScoringSteps = [
    {
      name: t("creationFlow.steps.createProject.name"),
      content: t("creationFlow.steps.createProject.content"),
      btnText: t("buttons.createProject"),
      component: (
        <FormikProvider value={projectFormikValues}>
          <ProjectCreationOverview
            errorMessage={
              (createProjectMutation.error?.response?.data as { non_field_errors: string[] } | undefined)
                ?.non_field_errors?.[0]
            }
          />
        </FormikProvider>
      ),
    },
    {
      name: t("creationFlow.steps.uploadTrainingData.name"),
      content: t("creationFlow.steps.uploadTrainingData.content"),
      btnText: t("buttons.trainModel"),
      component: (
        <UploadTrainingDataFlow
          currentProjectId={createdProjectId}
          onDataChange={(datasetUUID: string) => {
            setTrainingDataId(datasetUUID)
          }}
        />
      ),
    },
  ]

  // Credit scoring steps with action buttons, description, etc...
  const creditScoringAutoMLSteps = [
    {
      name: t("creationFlow.steps.createProject.name"),
      content: t("creationFlow.steps.createProject.content"),
      btnText: t("buttons.createProject"),
      component: (
        <FormikProvider value={projectFormikValues}>
          <ProjectCreationOverview
            errorMessage={
              (createProjectMutation.error?.response?.data as { non_field_errors: string[] } | undefined)
                ?.non_field_errors?.[0]
            }
          />
        </FormikProvider>
      ),
    },
    {
      name: t("creationFlow.steps.uploadTrainingData.name"),
      content: t("creationFlow.steps.uploadTrainingData.content"),
      btnText: t("buttons.next"),
      component: (
        <FormikProvider value={AutoMlFormikValues}>
          <UploadTrainingDataFlow
            currentProjectId={createdProjectId}
            onDataChange={(datasetUUID: string) => {
              setTrainingDataId(datasetUUID)
            }}
            creditScoringType="auto-ml"
          />
        </FormikProvider>
      ),
    },
    {
      name: t("creationFlow.steps.createSchema.name"),
      content: t("creationFlow.steps.createSchema.content"),
      btnText: t("buttons.trainModel"),
      component: (
        <FormikProvider value={AutoMlFormikValues}>
          <DataMappingFlow
            onDataChange={onAutoMlDataChange}
            createdProjectId={createdProjectId}
            fileHeaders={trainingSchema?.columns}
            isSchemaLoading={isTrainingSchemaLoading}
            errors={error}
            trainingDataId={trainingDataId}
          />
        </FormikProvider>
      ),
    },
  ]

  // credit scoring (pre-built flow) click actions function to be called when needed
  // to make it easier to than using switch cases and if conditions
  const creditScoringPreBuiltClickActions = (): void => {
    switch (activeStep) {
      case 0:
        projectFormikValues.submitForm()
        break
      case 1:
        handleTrainClick("pre-built")
        break
      default:
        break
    }
  }

  // credit scoring (auto-ml flow) click actions function to be called when needed
  // to make it easier to than using switch cases and if conditions
  const creditScoringAutoMLClickActions = (): void => {
    switch (activeStep) {
      case 0:
        projectFormikValues.submitForm()
        break
      case 1:
        setActiveStep(activeStep + 1)
        break
      case 2:
        AutoMlFormikValues.submitForm()
        break
      default:
        break
    }
  }

  /**
   * stepper helper function
   * to help disable the next btn when needed in the credit scoring project flow
   * @return  {boolean}
   */
  const shouldDisableCreditScoring = (): boolean => {
    switch (activeStep) {
      case 0:
        return !(projectFormikValues.dirty && projectFormikValues.isValid)
      case 1:
        return false
      default:
        return true
    }
  }

  // generic steps array to be mapped
  // to make it easier to than using switch cases and if conditions
  const genericSteps = [
    {
      name: t("creationFlow.steps.createProject.name"),
      content: t("creationFlow.steps.createProject.content"),
      btnText: t("buttons.createProject"),
      component: (
        <FormikProvider value={projectFormikValues}>
          <ProjectCreationOverview
            errorMessage={
              (createProjectMutation.error?.response?.data as { non_field_errors: string[] } | undefined)
                ?.non_field_errors?.[0]
            }
          />
        </FormikProvider>
      ),
    },
    {
      name: t("creationFlow.steps.addModel.name"),
      content: (
        <Typography variant="p">
          {t("creationFlow.steps.addModel.content", {
            documentationLink: (
              <Link
                href={`https://${getHostnames().docs}/`}
                target="_blank"
                style={{ color: theme.palette.base.text[1], textDecoration: "underline" }}
              >
                {t("creationFlow.steps.addModel.documentationText")}
              </Link>
            ),
          })}
        </Typography>
      ),
      btnText: createModelMutation.isSuccess ? t("buttons.integrate") : t("buttons.addModel"),
      component: (
        <FormikProvider value={modelFormikValues}>
          {createModelMutation.isLoading || isFailedLogs || createModelMutation.isSuccess ? (
            <PostCreationDialog
              creationErrors={
                (createModelMutation.error?.response?.data as { errors: Record<string, string>[] } | undefined)
                  ?.errors ?? []
              }
              creationLogs={
                createModelMutation.data?.data.container_logs ||
                (createModelMutation.error?.response?.data as { container_logs: string } | undefined)?.container_logs
              }
              isLoading={modelFormikValues.isSubmitting}
            />
          ) : (
            <ModelCreationForm errorMessage={modelCreationError} />
          )}
        </FormikProvider>
      ),
    },
  ]

  // generic click actions function to be called when needed
  // to make it easier to than using switch cases and if conditions
  const genericClickActions = (): void => {
    switch (activeStep) {
      case 0:
        projectFormikValues.submitForm()
        break
      case 1:
        createModelMutation.isSuccess
          ? navigate(`/projects/${createProjectMutation.data?.data.uuid}/integration/python`)
          : modelFormikValues.submitForm()
        break
      default:
        break
    }
  }

  const shouldDisableCreditScoringAutoml = (): boolean => {
    switch (activeStep) {
      case 0:
        return !(projectFormikValues.dirty && projectFormikValues.isValid)
      case 1:
        return (
          !(AutoMlFormikValues.dirty && modelFormikValues.isValid) ||
          !(trainingDataId !== "" && trainingDataId !== "sample")
        )
      case 2:
        return AutoMlFormikValues.values.target_column === ""
      default:
        return true
    }
  }

  /**
   * stepper helper function
   * to help disable the next btn when needed in the generic project flow
   * @return  {boolean}
   */
  const shouldDisableGeneric = (): boolean => {
    switch (activeStep) {
      case 0:
        return !(projectFormikValues.dirty && projectFormikValues.isValid)
      case 1:
        return !(modelFormikValues.dirty && modelFormikValues.isValid && !isFailedLogs)
      default:
        return true
    }
  }

  // classification steps array to be mapped
  // to make it easier to than using switch cases and if conditions
  const classificationSteps = [
    {
      name: t("creationFlow.steps.createProject.name"),
      content: t("creationFlow.steps.createProject.content"),
      btnText: t("buttons.createProject"),
      component: (
        <FormikProvider value={projectFormikValues}>
          <ProjectCreationOverview
            errorMessage={
              (createProjectMutation.error?.response?.data as { non_field_errors: string[] } | undefined)
                ?.non_field_errors?.[0]
            }
          />
        </FormikProvider>
      ),
    },
    {
      name: t("creationFlow.steps.addModel.name"),
      content: (
        <Typography variant="p">
          {t("creationFlow.steps.addModel.content", {
            documentationLink: (
              <Link
                href={`https://${getHostnames().docs}/`}
                target="_blank"
                style={{ color: theme.palette.base.text[1], textDecoration: "underline" }}
              >
                {t("creationFlow.steps.addModel.documentationText")}
              </Link>
            ),
          })}
        </Typography>
      ),
      btnText: createModelMutation.isSuccess ? t("buttons.testYourModel") : t("buttons.addModel"),
      component: (
        <FormikProvider value={modelFormikValues}>
          <Fragment>
            {createModelMutation.isLoading || isFailedLogs || createModelMutation.isSuccess ? (
              <PostCreationDialog
                creationErrors={
                  (createModelMutation.error?.response?.data as { errors: Record<string, string>[] } | undefined)
                    ?.errors
                }
                creationLogs={
                  createModelMutation.data?.data.container_logs ||
                  (createModelMutation.error?.response?.data as { container_logs: string } | undefined)?.container_logs
                }
                isLoading={createModelMutation.isLoading}
              />
            ) : (
              <ModelCreationForm errorMessage={modelCreationError} />
            )}
          </Fragment>
        </FormikProvider>
      ),
    },
    {
      name: t("creationFlow.steps.testRequest.name"),
      content: t("creationFlow.steps.testRequest.content"),
      btnText: t("buttons.chooseTarget"),
      component: (
        <FormikProvider value={DryRunFormikValues}>
          <DryRunForm
            response={responseData}
            requestErrorMessage={
              requestJsonError
                ? t("creationFlow.errors.requestNotJson")
                : (predictionsTest.error?.response?.data.details as string)
            }
            isRequestError={requestJsonError}
            isMutationError={predictionsTest.isError}
            responseStatusCode={!requestJsonError ? predictionsTest.error?.response?.status : undefined}
            isLoadingRequest={isLoadingOpenApiData}
          />
        </FormikProvider>
      ),
    },
    {
      name: t("creationFlow.steps.configureTarget.name"),
      content: t("creationFlow.steps.configureTarget.content"),
      btnText: classificationConfigurationMutation.isSuccess ? t("buttons.integrate") : t("buttons.configureTarget"),
      component: (
        <FormikProvider value={configurationFormikValues}>
          <ConfigurationForm
            responseJSON={predictionsTest.data?.data.output as Record<string, string>}
            modelId={createModelMutation.data?.data.model.uuid as string}
            errorMessage={classificationConfigurationMutation.error?.response?.data.details}
          />
        </FormikProvider>
      ),
    },
  ]

  // classification click actions function to be called when needed
  // to make it easier to than using switch cases and if conditions
  const classificationClickActions = (): void => {
    switch (activeStep) {
      case 0:
        projectFormikValues.submitForm()
        break
      case 1:
        createModelMutation.isSuccess ? setActiveStep(activeStep + 1) : modelFormikValues.submitForm()
        break
      case 2:
        predictionsTest.isSuccess && setActiveStep(activeStep + 1)
        break
      case 3:
        classificationConfigurationMutation.isSuccess
          ? navigate(`/projects/${createProjectMutation.data?.data.uuid}/integration/python`)
          : configurationFormikValues.submitForm()
        break
      default:
        break
    }
  }

  /**
   * stepper helper function
   * to help disable the next btn when needed in the classification project flow
   * @return  {boolean}
   */
  const shouldDisableClassification = (): boolean => {
    switch (activeStep) {
      case 0:
        return !(projectFormikValues.dirty && projectFormikValues.isValid)
      case 1:
        return !(modelFormikValues.dirty && modelFormikValues.isValid)
      case 2:
        return !predictionsTest.isSuccess
      case 3:
        return !(configurationFormikValues.isValid && configurationFormikValues.dirty)
      default:
        return true
    }
  }

  type stepType = {
    name: string
    content: string | React.ReactNode
    btnText: string
    component: React.ReactNode
  }

  type stepProps = {
    type?: string
  }

  /**
   * stepper helper function to get the steps of the flow given the project type
   * @param  {string} type type of the project
   * @return  {Array<stepType>}
   */
  function getSteps(props: stepProps): Array<stepType> {
    const { type } = props

    switch (type) {
      case "generic":
        return genericSteps
      case "classification":
        return classificationSteps
      case "credit_scoring_pre-built":
        return creditScoringSteps
      case "credit_scoring_auto-ml":
        return creditScoringAutoMLSteps
      default:
        return creditScoringAutoMLSteps
    }
  }

  const steps = getSteps({ type: projectFormikValues.values.type })

  // reroutes user to training jobs page after model training is initiated
  useEffect(() => {
    if (
      (trainModelUsingPrebuiltModel.isSuccess && !trainModelUsingPrebuiltModel.isError) ||
      (isAutomlSuccess && !isAutoMlError)
    ) {
      setTimeout(() => {
        navigate({
          pathname: `/projects/${createProjectMutation.data?.data.uuid}/model-training`,
          search: `tab=${mapResponseTypesToTabValues(
            trainModelUsingPrebuiltModel?.data?.data?.status ?? trainingModelAutomlData?.data?.status,
          )}`,
        })
      }, 500)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createProjectMutation, isLoading, isAutoMlError, isAutomlSuccess, trainModelUsingPrebuiltModel])

  /**
   * Getting Predictions object from the helper function then convert it to JSON
   */
  useEffect(() => {
    if (dryRunRequest?.data) {
      try {
        const extractedObjectJSON = JSON.stringify(extractPredictionKeysFromOpenapi(dryRunRequest?.data), null, 2)
        DryRunFormikValues.setFieldValue("request", extractedObjectJSON)
      } catch (_) {
        DryRunFormikValues.setFieldValue("request", "{}")
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dryRunRequest?.data])

  return (
    <div className="layoutPadding">
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={2}
        className={Styles.creationFlowRoot}
      >
        <Grid item xs={12} md={9}>
          <Card className="card-box-shadow">
            <CardHeader
              title={<Typography variant="h2-bold">{t("creationFlow.title")}</Typography>}
              className={Styles.flowHeader}
              action={
                <IconButton
                  aria-label="close"
                  onClick={() =>
                    isFailedLogs ? createModelMutation.reset() : navigate("/projects?page=model", { replace: true })
                  }
                >
                  {isFailedLogs ? (
                    <ArrowBackIcon style={{ color: theme.palette.base.text[2] }} />
                  ) : (
                    <CloseIcon style={{ color: theme.palette.base.text[2] }} />
                  )}
                </IconButton>
              }
            />
            <Grid className={Styles.card}>{steps[activeStep]?.component}</Grid>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((step) => (
              <Step key={step.name}>
                <StepLabel>
                  <Typography variant="a">{step.name}</Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="p">{step.content}</Typography>
                  <div className={Styles.actionsContainer}>
                    <Button
                      id="cancel_button"
                      variant="secondary"
                      onClick={() =>
                        isFailedLogs ? createModelMutation.reset() : navigate("/projects?page=model", { replace: true })
                      }
                    >
                      {t("buttons.cancel")}
                    </Button>
                    {activeStep === 2 && projectFormikValues.values.type !== "credit_scoring_auto-ml" && (
                      <Button
                        variant="secondary"
                        disabled={
                          !(DryRunFormikValues.isValid && DryRunFormikValues.dirty) || predictionsTest.isLoading
                        }
                        onClick={() => {
                          DryRunFormikValues.submitForm()
                        }}
                        id="execute"
                        isLoading={predictionsTest.isLoading}
                      >
                        {t("buttons.execute")}
                      </Button>
                    )}
                    {activeStep === 3 && projectFormikValues.values.type !== "credit_scoring_auto-ml" && (
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setActiveStep(activeStep - 1)
                        }}
                        style={Styles.btnMargins as React.CSSProperties}
                        id="retest"
                      >
                        {t("buttons.retest")}
                      </Button>
                    )}

                    <Button
                      id="next_button"
                      variant="primary"
                      disabled={shouldDisableNext() || isBtnLoading}
                      onClick={() => {
                        switch (projectFormikValues.values.type) {
                          case "generic":
                            return genericClickActions()
                          case "classification":
                            return classificationClickActions()
                          case "credit_scoring_pre-built":
                            return creditScoringPreBuiltClickActions()
                          case "credit_scoring_auto-ml":
                            return creditScoringAutoMLClickActions()
                          default:
                            return creditScoringPreBuiltClickActions()
                        }
                      }}
                      isLoading={isBtnLoading}
                    >
                      {step.btnText}
                    </Button>
                  </div>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Grid>
      </Grid>
    </div>
  )
}
